import { OnDataLoadProps } from '@turntable/core'
import qs from 'query-string'
import { useCallback, useEffect, useMemo, useReducer, useState } from 'react'
import Confetti from 'react-confetti'
import { Colors } from 'src/resources/colors'
import { LogoAvatar } from 'src/applications/Embed/components/LogoAvatar'
import { OverlayHeader } from 'src/applications/Embed/components/OverlayHeader'
import { DataTable } from 'src/applications/Oversight/components/DataTable'
import { EmbedLayout } from 'src/applications/Embed/fragments/Layout'
import { Overlay } from 'src/applications/Embed/fragments/Overlay'
import { IScenePayload, SCENE } from 'src/applications/Embed/types/IScene'
import { useFetchWorkbookRows } from 'src/applications/Oversight/hooks/useFetchWorkbookRows'
import { StatusTab } from 'src/applications/Embed/components/StatusTab'
import { StatusTabGroup } from 'src/applications/Embed/components/StatusTabGroup'
import { FlatButton } from 'src/resources/elements/buttons/FlatButton'
import { Validation } from 'src/resources/hooks/useTableFilters'
import { BrandedInfiniteLoader } from 'src/resources/lottie/BrandedInfiniteLoader'
import { useSmartMutation } from 'src/smart/hooks/useSmartMutation'
import { useSmartQuery } from 'src/smart/hooks/useSmartQuery'
import { SM_SUBMIT_BATCH } from 'src/smart/mutations/SM_SUBMIT_BATCH'
import { SQ_GET_SUBMISSION_PROGRESS } from 'src/smart/queries/SQ_GET_SUBMISSION_PROGRESS'
import { useWorkbookUpdatedSubscription } from 'src/smart/queries/SQ_WORKBOOK_UPDATED_SUBSCRIPTION'
import { useTheme } from 'styled-components'

import {
  FlexContainer,
  ButtonGroup,
  GoBackButton,
  StaticTab,
  SingleTabContainer,
  VerticalLine,
  EllipsisLoader
} from 'src/applications/Embed/elements/SceneElements'
import * as Sentry from '@sentry/react'
import i18next from 'i18next'
import { useTranslation } from 'react-i18next'
const PAGE_SIZE = 500

export function FinalScene({ team, batch, sheet, onGoBack }: Partial<IScenePayload>) {
  const { t } = useTranslation()
  const theme: any = useTheme()
  const schemaObj = sheet?.template?.jsonSchema?.schema ?? batch?.schema

  const [buttonDisabled, setButtonDisabled] = useState(false)
  const [validationState, setValidationState] = useState<Validation>('review')
  const [dataTableKey, forceDataTableUpdate] = useReducer((key) => key + 1, 0)

  const submitBatchAndDismiss = useSmartMutation(SM_SUBMIT_BATCH)
  const { result, state } = useSmartQuery(SQ_GET_SUBMISSION_PROGRESS, {
    fetchPolicy: 'network-only',
    variables: { batchId: batch.batchId }
  })

  const useCopyUpdates = batch.features?.includes('SUBMIT_FLOW_COPY')

  const {
    accepted,
    totalRows,
    review,
    filtered,
    rows,
    labels,
    fetchMoreRows,
    refetchRows,
    loading
  } = useFetchWorkbookRows(
    batch.workbookId,
    '',
    validationState,
    schemaObj,
    parseInt(batch.schemaId, 10),
    PAGE_SIZE,
    undefined,
    undefined,
    { fetchPolicy: 'network-only' }
  )

  const rowsRemaining = result?.rowsRemaining
  const submissionComplete = rowsRemaining === 0

  if (accepted === undefined && !loading) {
    Sentry.captureMessage(
      'Occurance of accepted count being undefined and loading being false - 7_FinalScene.tsx'
    )
    refetchRows()
  }

  useEffect(() => {
    if (submissionComplete) {
      state.stopPolling()
      refetchRows()
    } else {
      state.startPolling(1000)
    }
  }, [submissionComplete, state])

  useEffect(() => {
    if (submissionComplete && review === 0) {
      setValidationState('accepted')
    }
  }, [submissionComplete, review])

  useWorkbookUpdatedSubscription(batch.workbookId, {
    onSubscriptionData: async () => {
      await refetchRows()
      forceDataTableUpdate()
    }
  })

  const handleOnDataLoad = useCallback(
    async ({ pageSize, page }: OnDataLoadProps) => {
      const skip = (page - 1) * pageSize < 0 ? 0 : (page - 1) * pageSize

      return fetchMoreRows({
        skip,
        limit: pageSize
      })
    },
    [fetchMoreRows]
  )

  const handleClickSubmit = useCallback(async () => {
    setButtonDisabled(true)
    await submitBatchAndDismiss
      .run({
        batchId: batch.batchId,
        dismissRemainingRecords: true
      })
      .catch(() => setButtonDisabled(false))
  }, [batch.batchId, setButtonDisabled, submitBatchAndDismiss])

  const submitText = useMemo(() => {
    if (
      theme.textOverrides.ctaLabelFinal &&
      !theme.textOverrides.ctaLabelFinal.includes('textOverrides')
    ) {
      return t(theme.textOverrides.ctaLabelFinal)
    }

    return t('elements.buttons.submit')
  }, [batch.status])

  const customTheme = useMemo(() => {
    const ct = qs.parse(location.search)?.theme
    if (ct && typeof ct === 'string') {
      try {
        console.log({ ct: JSON.parse(ct) })
        return JSON.parse(ct)
      } catch (error) {
        console.error('Unable to parse custom theme.')
      }
    }
    return null
  }, [location.search])

  const teamName = customTheme?.displayName ?? team.name
  const showReviewMessaging = review > 0
  const showConfetti = submissionComplete && !customTheme?.hideConfetti && !showReviewMessaging
  const showTable = submissionComplete && !!batch.schema?.properties && !!rows?.length
  const headerText = useCopyUpdates
    ? t('scenes.7_FinalScene.aboutToSubmit', { count: accepted })
    : accepted === 1
    ? t(theme.textOverrides.successfulImport_one, { count: accepted })
    : t(theme.textOverrides.successfulImport_other, { count: accepted })
  const recordText = useCopyUpdates
    ? t('scenes.7_FinalScene.recordCount', { count: totalRows })
    : totalRows === 1
    ? t(theme.textOverrides.numberSubmitted_one, { count: totalRows })
    : t(theme.textOverrides.numberSubmitted_other, { count: totalRows })
  const submissionCompleteText = useMemo(() => {
    /*
      Display custom submit text if provided via SDK, if not, we want to display
      help text ONLY if there are rows left in review
    */
    if (customTheme?.submitCompleteText) {
      return customTheme?.submitCompleteText
    }

    if (showReviewMessaging) {
      return i18next.t('scenes.7_FinalScene.showReviewMessage', { teamName: teamName })
    }

    return ''
  }, [teamName, showReviewMessaging])

  return (
    <EmbedLayout>
      <FlexContainer>
        {showConfetti && (
          <Confetti
            colors={[
              Colors.brandPrimary,
              Colors.brandPrimaryMidnight,
              Colors.brandSecondary,
              Colors.brandAccent,
              Colors.border
            ]}
            numberOfPieces={200}
            recycle={false}
          />
        )}
        <Overlay>
          <>
            {!loading &&
              (submissionComplete ? (
                <>
                  {batch.synced && <LogoAvatar logo={customTheme?.logo} name={teamName} />}
                  <OverlayHeader title={t(headerText)} description={submissionCompleteText} />
                  <StatusTabGroup>
                    <StaticTab>{recordText}</StaticTab>
                    <VerticalLine />
                    <StatusTab
                      status={{
                        label: t(theme.textOverrides.finalViewImportedTab),
                        value: 'accepted'
                      }}
                      onClick={() => setValidationState('accepted')}
                      count={accepted}
                      active={validationState === 'accepted'}
                    />
                    <StatusTab
                      status={{
                        label: t('scenes.7_FinalScene.rejectedStatusLabel'),
                        value: 'review'
                      }}
                      onClick={() => setValidationState('review')}
                      count={review}
                      active={validationState === 'review'}
                      rejected
                    />
                  </StatusTabGroup>
                </>
              ) : (
                <>
                  <BrandedInfiniteLoader name={teamName} />
                  <OverlayHeader
                    title={<EllipsisLoader text={t(theme.textOverrides.loaderMessageImporting)} />}
                    description={customTheme?.loadingText}
                  />
                  {!theme.hideImportedCounter && (
                    <SingleTabContainer>
                      <StatusTab
                        status={{ label: t('scenes.7_FinalScene.rowsRemaining') }}
                        count={rowsRemaining}
                        active
                      />
                    </SingleTabContainer>
                  )}
                </>
              ))}
            {showTable && (
              <>
                <DataTable
                  key={dataTableKey}
                  pageSize={PAGE_SIZE}
                  tableHeaders={labels}
                  tableRows={rows}
                  totalCount={filtered ?? totalRows ?? rows.length}
                  onDataLoad={handleOnDataLoad}
                  tableHeight='250px'
                  readOnly
                />
                <ButtonGroup>
                  {showReviewMessaging && (
                    <GoBackButton onClick={() => onGoBack(SCENE.REVIEW)}>
                      {t(theme.textOverrides.finalViewReviewRejectedButton)}
                    </GoBackButton>
                  )}
                  <FlatButton
                    color='primary'
                    disabled={buttonDisabled}
                    onClick={handleClickSubmit}
                  >
                    {submitText}
                  </FlatButton>
                </ButtonGroup>
              </>
            )}
          </>
        </Overlay>
      </FlexContainer>
    </EmbedLayout>
  )
}
